import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../pages/RosePage.css';

const POLL_MS = 15000;

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function channelNote(item) {
  if (item.online_quantity > 0 && item.counter_quantity > 0) {
    return `${item.online_quantity} online · ${item.counter_quantity} counter`;
  }
  return '';
}

function tvColumns(count) {
  if (count <= 1) return 1;
  return Math.max(1, Math.round(Math.sqrt(count * 1.35)));
}

export default function SalesBoard({
  kicker,
  documentTitle,
  emptyMessage,
  load,
  accent,
  variant,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [bumped, setBumped] = useState({});
  const [standalone, setStandalone] = useState(isStandalone);
  const quantities = useRef({});
  const bumpTimer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const next = await load();
      const highlights = {};
      for (const item of next.items || []) {
        const prev = quantities.current[item.id];
        if (prev !== undefined && item.quantity > prev) highlights[item.id] = true;
        quantities.current[item.id] = item.quantity;
      }
      setData(next);
      setError('');
      if (Object.keys(highlights).length) {
        setBumped(highlights);
        clearTimeout(bumpTimer.current);
        bumpTimer.current = setTimeout(() => setBumped({}), 1600);
      }
    } catch (err) {
      setError(err.message || 'Could not refresh');
    }
  }, [load]);

  useEffect(() => {
    document.title = documentTitle;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      clearTimeout(bumpTimer.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh, documentTitle]);

  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    const onChange = () => setStandalone(isStandalone());
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (variant !== 'tv') return undefined;
    document.documentElement.classList.add('tv-lock');
    return () => document.documentElement.classList.remove('tv-lock');
  }, [variant]);

  const categories = [];
  const byCategory = new Map();
  for (const item of data?.items || []) {
    const name = item.category_name || kicker;
    if (!byCategory.has(name)) {
      byCategory.set(name, []);
      categories.push(name);
    }
    byCategory.get(name).push(item);
  }
  const showCategories = categories.length > 1;
  const soldLine = data
    ? (data.since_label === 'Today' ? 'Sold today' : `Sold ${data.since_label.charAt(0).toLowerCase()}${data.since_label.slice(1)}`)
    : 'Loading…';

  if (variant === 'tv') {
    const count = data?.items?.length || 0;
    const cols = tvColumns(count);
    const rows = Math.max(1, Math.ceil(count / cols));
    return (
      <main
        className={`tv-board${accent ? ` accent-${accent}` : ''}`}
        style={{ '--cols': cols, '--rows': rows }}
      >
        <header className="tv-head">
          <div>
            <p className="tv-kicker">{kicker}</p>
            <p className="tv-since">{soldLine}</p>
          </div>
        </header>

        {!data && !error && <p className="rose-status">Loading sales…</p>}
        {error && (
          <p className="rose-status rose-status-err">
            {data ? 'Could not refresh. These are the last numbers.' : error}
          </p>
        )}
        {data && data.items.length === 0 && (
          <p className="rose-status">{emptyMessage}</p>
        )}

        {count > 0 && (
          <div className="tv-grid">
            {data.items.map((item) => {
              const tracked = item.prepared != null;
              const quiet = item.quantity <= 0 && !tracked;
              return (
              <div key={item.id} className={`tv-tile${quiet ? ' is-zero' : ''}${bumped[item.id] ? ' is-up' : ''}`}>
                <div className="tv-name">
                  {showCategories && item.category_name && <em>{item.category_name}</em>}
                  <span>{item.name}</span>
                </div>
                <span className="tv-qty">
                  <span className="tv-frac">
                    {tracked ? (
                      <>
                        <span className="tv-sold">{item.quantity}</span>
                        <span className="tv-of">/{item.prepared}</span>
                      </>
                    ) : item.quantity}
                  </span>
                  {tracked && (
                    <span className={`tv-left${item.remaining === 0 ? ' is-finished' : ''}`}>
                      {item.remaining === 0 ? 'Sold out' : `${item.remaining} NOT Sold`}
                    </span>
                  )}
                </span>
              </div>
              );
            })}
          </div>
        )}

        {data && (
          <p className="tv-foot">
            <span className="rose-live" aria-hidden />
            Updated {data.updated_at}
          </p>
        )}
      </main>
    );
  }

  return (
    <main className={`rose-page${accent ? ` accent-${accent}` : ''}`}>
      <header className="rose-head">
        <p className="rose-kicker">{kicker}</p>
        <h1>{data ? data.total : '—'}</h1>
        <p className="rose-since">
          {data
            ? (data.since_label === 'Today' ? 'Sold today' : `Sold ${data.since_label.charAt(0).toLowerCase()}${data.since_label.slice(1)}`)
            : 'Loading…'}
        </p>
      </header>

      {!data && !error && <p className="rose-status">Loading sales…</p>}
      {error && (
        <p className="rose-status rose-status-err">
          {data ? 'Could not refresh. These are the last numbers.' : error}
        </p>
      )}

      {data && data.items.length === 0 && (
        <p className="rose-status">{emptyMessage}</p>
      )}

      {categories.map((category) => (
        <section key={category} className="rose-section">
          {showCategories && <h2>{category}</h2>}
          <ul>
            {byCategory.get(category).map((item) => (
              <li key={item.id} className={item.quantity > 0 ? '' : 'is-zero'}>
                <div className="rose-item-name">
                  <span>{item.name}</span>
                  {channelNote(item) && <small>{channelNote(item)}</small>}
                </div>
                <span className={`rose-qty${bumped[item.id] ? ' is-up' : ''}`}>{item.quantity}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {data && (
        <p className="rose-updated">
          <span className="rose-live" aria-hidden />
          Updated {data.updated_at}
        </p>
      )}

      {!standalone && (
        <p className="rose-install">
          To keep this on your phone, tap Share, then Add to Home Screen.
        </p>
      )}
    </main>
  );
}
